import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { getApiUrl } from '../../utils/config';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Users, 
  UserPlus, 
  Crown, 
  Shield, 
  Edit3, 
  Eye, 
  User,
  MoreHorizontal,
  Mail,
  Calendar,
  Trash2,
  HelpCircle,
  Zap,
  ArrowRight
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { InviteUserModal } from './InviteUserModal';
import { EditRoleModal } from './EditRoleModal';
import { PendingInvitationsCard } from './PendingInvitationsCard';
import { UserPendingInvitationsCard } from './UserPendingInvitationsCard';
import { motion, AnimatePresence } from 'framer-motion';
import waitlistService from '../../services/waitlistService';

interface TeamMember {
  id: string;
  user_id: string;
  client_id: string;
  role: string;
  created_at: string;
  user_email?: string;
  user_username?: string;
  user_full_name?: string;
  inviter?: {
    username: string;
  };
}

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'owner': return <div className="w-4 h-4" />;
    case 'admin': return <Shield className="w-4 h-4" />;
    case 'editor': return <Edit3 className="w-4 h-4" />;
    case 'member': return <User className="w-4 h-4" />;
    case 'viewer': return <Eye className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'owner': return 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 dark:from-yellow-900/50 dark:to-amber-900/50 dark:text-yellow-200';
    case 'admin': return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 dark:from-red-900/50 dark:to-rose-900/50 dark:text-red-200';
    case 'editor': return 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 dark:from-blue-900/50 dark:to-purple-900/50 dark:text-blue-200';
    case 'member': return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 dark:from-green-900/50 dark:to-emerald-900/50 dark:text-green-200';
    case 'viewer': return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/50 dark:to-slate-900/50 dark:text-gray-200';
    default: return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/50 dark:to-slate-900/50 dark:text-gray-200';
  }
};

const StatCard = ({ icon: Icon, name, value, color, className = '' }) => (
  <motion.div 
    className={`relative overflow-hidden rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 transition-all duration-300 group ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    whileHover={{ scale: 1.03, y: -8 }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 dark:from-blue-500/5 dark:via-purple-500/5 dark:to-slate-500/5"></div>
    <div className="relative p-3 sm:p-4 lg:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
          <div className="relative">
            <div className="relative p-2 sm:p-2.5 lg:p-3 bg-gradient-to-br from-slate-100 to-slate-200/80 dark:from-slate-700/60 dark:to-slate-600/60 backdrop-blur-sm rounded-lg sm:rounded-xl border-0">
              <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${color}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider truncate">{name}</p>
            <p className="text-xl sm:text-2xl lg:text-3xl font-black text-black dark:text-white mt-1 transition-colors" onMouseEnter={(e) => e.currentTarget.style.color = '#30b3dd'} onMouseLeave={(e) => e.currentTarget.style.color = ''}>{value}</p>
          </div>
        </div>

      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
  </motion.div>
);

export const TeamManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editRoleModal, setEditRoleModal] = useState<{ open: boolean; member?: TeamMember }>({ open: false });
  const [teamLimits, setTeamLimits] = useState<{
    current_count: number;
    limit_value: number;
    remaining: number;
    allowed: boolean;
    reset_period: string;
  } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);

  useEffect(() => {
    if (currentClient) {
      fetchTeamMembers();
      fetchTeamLimits();
    } else {
      setLoading(false);
    }
  }, [currentClient]);

  const fetchTeamMembers = async () => {
    if (!currentClient) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        const members = await response.json();
        
        // Validate and filter members with required data
        const validMembers = members.filter(member => 
          member && 
          member.user_email && 
          member.user_id && 
          member.role
        );
        
        setTeamMembers(validMembers);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || `Failed to fetch team members: ${response.status}`);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load team members",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamLimits = async () => {
    if (!currentClient) {
      return;
    }
    
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/limits`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        const limits = await response.json();
        setTeamLimits(limits);
      } else {
        console.warn('Failed to fetch team limits:', response.status);
      }
    } catch (error) {
      console.warn('Error fetching team limits:', error);
    }
  };

  const handleInviteClick = () => {
    // Check if team member limit is reached
    if (teamLimits && !teamLimits.allowed && teamLimits.limit_value !== -1) {
      setShowUpgradeModal(true);
    } else {
      setInviteModalOpen(true);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!currentClient) return;
    
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/clients/${currentClient.id}/team/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        toast({
          title: "Success",
          description: "Team member removed successfully",
        });
        fetchTeamMembers();
        fetchTeamLimits();
      } else {
        throw new Error('Failed to remove team member');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove team member",
        variant: "destructive",
      });
    }
  };

  const handleJoinWaitlist = async () => {
    if (!user?.email) {
      toast({
        title: "Error",
        description: "User email not found",
        variant: "destructive",
      });
      return;
    }

    setIsJoiningWaitlist(true);
    
    try {
      const response = await waitlistService.joinWaitlist({
        email: user.email,
        feature_name: 'Team Collaboration Plans',
        user_name: user.first_name && user.last_name 
          ? `${user.first_name} ${user.last_name}` 
          : user.username || undefined,
        additional_info: `User reached team member limit of ${teamLimits?.limit_value} members and is interested in higher tier plans.`
      });

      if (response.success) {
        toast({
          title: "Success! 🎉",
          description: "You've been added to the waitlist. We'll notify you when higher tier plans are available!",
        });
        setShowUpgradeModal(false);
      } else {
        throw new Error(response.message || 'Failed to join waitlist');
      }
    } catch (error: any) {
      console.error('Waitlist join error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to join waitlist. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <motion.div 
          className="flex flex-col items-center space-y-4"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400"></div>
          <p className="text-slate-600 dark:text-slate-400 font-medium">Loading team members...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Page Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-2 sm:px-4 lg:px-8 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-grow"
            >
              <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl lg:rounded-2xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)', boxShadow: '0 10px 25px rgba(48, 179, 221, 0.25)' }}>
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black bg-clip-text text-transparent" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    Team Management
                  </h1>
                  <p className="hidden sm:block text-slate-600 dark:text-slate-300 text-sm lg:text-lg">Manage team members and their roles</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Button 
                onClick={handleInviteClick} 
                className="text-white border-0 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
                style={{ 
                  background: 'linear-gradient(to right, #30b3dd, #4a298a)',
                  boxShadow: '0 10px 25px rgba(48, 179, 221, 0.25)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, #2891b8, #3a1f6b)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(48, 179, 221, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(48, 179, 221, 0.25)';
                }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Invite Member</span>
                <span className="sm:hidden">Invite</span>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="px-2 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6 space-y-4 sm:space-y-6 lg:space-y-8">

        {/* Team Stats */}
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 lg:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <StatCard 
            icon={Users} 
            name="Total Members" 
            value={teamMembers.length} 
            color="text-indigo-600 dark:text-indigo-400"
          />
          <StatCard 
            icon={Crown} 
            name="Owners" 
            value={teamMembers.filter(m => m.role === 'owner').length} 
            color="text-yellow-600 dark:text-yellow-400"
          />
          <StatCard 
            icon={Shield} 
            name="Admins" 
            value={teamMembers.filter(m => m.role === 'admin').length} 
            color="text-red-600 dark:text-red-400"
          />
          <StatCard 
            icon={Edit3} 
            name="Editors" 
            value={teamMembers.filter(m => m.role === 'editor').length} 
            color="text-blue-600 dark:text-blue-400"
          />
        </motion.div>

        {/* Team Member Usage - Minimal Modern Design */}
        {teamLimits && (
          <motion.div 
            className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200 dark:border-slate-600/40 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50/50 via-indigo-50/30 to-blue-50/30 dark:from-indigo-500/5 dark:via-blue-500/5 dark:to-slate-500/5"></div>
            <div className="relative p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #30b3dd, #4a298a)' }}>
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-800 dark:text-white">Team Usage</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {teamLimits.limit_value === -1 
                        ? 'Unlimited members' 
                        : `${teamLimits.current_count}/${teamLimits.limit_value} members`
                      }
                    </p>
                  </div>
                </div>
                
                {teamLimits.limit_value !== -1 && (
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-800 dark:text-white">
                      {teamLimits.remaining}
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-400">
                      remaining
                    </div>
                  </div>
                )}
              </div>
              
              {teamLimits.limit_value !== -1 && (
                <div className="mt-4 space-y-3">
                  <div className="relative">
                    <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                      <motion.div 
                        className={`h-full rounded-full transition-all duration-500 ${
                          teamLimits.remaining > 0 
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600' 
                            : 'bg-gradient-to-r from-orange-500 to-red-500'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ 
                          width: `${Math.min((teamLimits.current_count / teamLimits.limit_value) * 100, 100)}%` 
                        }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                      />
                    </div>
                  </div>
                  
                  {!teamLimits.allowed && (
                    <motion.div 
                      className="flex items-center space-x-2 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.6 }}
                    >
                      <div className="w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <HelpCircle className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm text-orange-700 dark:text-orange-300">
                        Team limit reached. Upgrade to invite more members!
                      </p>
                    </motion.div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* User's Pending Invitations */}
        <UserPendingInvitationsCard onInvitationAccepted={fetchTeamMembers} />

        {/* Pending Invitations (Admin View) */}
        <PendingInvitationsCard onInvitationChange={fetchTeamMembers} />

        {/* Team Members List */}
        <motion.div 
          className="relative rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 transition-all duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/30 dark:from-indigo-500/5 dark:via-blue-500/5 dark:to-slate-500/5"></div>
          <div className="relative p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">Team Members</h2>
              <div className="flex items-center space-x-1 sm:space-x-2 text-slate-600 dark:text-slate-400">
                <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">{teamMembers.length} members</span>
              </div>
            </div>
            
            <div className="space-y-2 sm:space-y-4">
              <AnimatePresence>
                {teamMembers.length === 0 ? (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">No team members found</p>
                    <p className="text-slate-400 dark:text-slate-500 text-sm mt-1">You might be the only member of this client</p>
                  </motion.div>
                ) : (
                  teamMembers.map((member, index) => {
                    // Safety check for member data
                    if (!member || !member.user_email) {
                      return null;
                    }
                    
                    return (
                      <motion.div 
                        key={member.id}
                        className="group relative rounded-xl bg-white/60 dark:bg-slate-700/40 backdrop-blur-sm border border-slate-200/40 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-700/60 transition-all duration-300 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/20"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ scale: 1.01, y: -2 }}
                      >
                        <div className="p-3 sm:p-4 lg:p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 flex-1 min-w-0">
                              <div className="relative">
                                <Avatar className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ring-2 ring-white/50 dark:ring-slate-600/50">
                                  <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${member.user_username || 'user'}`} />
                                  <AvatarFallback className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 text-indigo-700 dark:text-indigo-300 font-semibold">
                                    {member.user_full_name?.split(' ').map(n => n[0]).join('') || member.user_username?.slice(0, 2).toUpperCase() || 'U'}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white dark:border-slate-700"></div>
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3 mb-1 sm:mb-2">
                                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-100 truncate">
                                    {member.user_full_name || member.user_username}
                                  </h3>
                                  <Badge className={`${getRoleColor(member.role)} border-0 shadow-sm text-xs`}>
                                    <div className="flex items-center space-x-1">
                                      {getRoleIcon(member.role)}
                                      <span className="capitalize font-medium">{member.role}</span>
                                    </div>
                                  </Badge>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                                  <div className="flex items-center space-x-1">
                                    <Mail className="w-3 h-3" />
                                    <span className="truncate">{member.user_email}</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>Joined {new Date(member.created_at).toLocaleDateString()}</span>
                                  </div>
                                  {member.inviter && (
                                    <span className="text-xs bg-slate-100 dark:bg-slate-600 px-2 py-1 rounded-full">
                                      Invited by {member.inviter.username}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="opacity-100 transition-opacity duration-200 hover:bg-slate-100 dark:hover:bg-slate-600"
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent align="end" className="w-48 z-[9999]" sideOffset={5}>
                                <button 
                                  onClick={() => setEditRoleModal({ open: true, member })}
                                  disabled={member.role === 'owner'}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md flex items-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Edit3 className="w-4 h-4 mr-2" />
                                  Change Role
                                </button>
                                <button 
                                  onClick={() => handleRemoveMember(member.user_id)}
                                  disabled={member.role === 'owner'}
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-md flex items-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove Member
                                </button>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </motion.div>
                    );
                  })
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Modals */}
      <InviteUserModal 
        isOpen={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => {
          fetchTeamMembers();
          fetchTeamLimits();
        }}
      />
      
      <EditRoleModal
        isOpen={editRoleModal.open}
        member={editRoleModal.member}
        onClose={() => setEditRoleModal({ open: false })}
        onSuccess={() => {
          fetchTeamMembers();
          fetchTeamLimits();
        }}
      />

      {/* Upgrade Modal - Coming Soon */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <span>Team Member Limit Reached</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                You've reached your team member limit of {teamLimits?.limit_value} member{teamLimits?.limit_value !== 1 ? 's' : ''}.
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Current plan: <span className="font-medium">{user?.tier?.display_name || 'Free Tier'}</span>
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Crown className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-1">Upgrade to Add More Team Members</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    Upgrade to a higher tier plan to expand your team limits and unlock advanced collaboration features.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full sm:w-auto"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/pricing');
              }}
              className="w-full sm:w-auto text-white"
              style={{ 
                background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                backgroundSize: '200% 200%',
                backgroundPosition: 'left center',
                boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundPosition = 'right center';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4)';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundPosition = 'left center';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3)';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Plan
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};